import React, { useMemo, useState } from "react";
import { Check, Edit2, Loader2, MessageSquareText, Trash2, X } from "lucide-react";
import { useDossierNotes } from "../hooks/useDossierNotes";
import type { User, UserRole } from "../types";

interface DossierNotesProps {
  dossierId: string;
  currentUser: User;
  className?: string;
}

const roleLabelMap: Record<UserRole, string> = {
  admin: "Administrateur",
  partner: "Partenaire",
  installer: "Poseur",
  commercial: "Commercial",
};

const formatNoteDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { date: value, time: "" };
  }

  return {
    date: date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
    time: date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

export function DossierNotes({ dossierId, currentUser, className = "" }: DossierNotesProps) {
  const [content, setContent] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState("");
  const [savingEditId, setSavingEditId] = useState<string | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);

  const { notes, loading, submitting, error, addNote, updateNote, deleteNote } = useDossierNotes({
    dossierId,
    currentUser,
  });

  const canSubmit = content.trim().length > 0 && !submitting;
  const noteCountLabel = useMemo(
    () => `${notes.length} note${notes.length > 1 ? "s" : ""}`,
    [notes.length]
  );

  const handleSubmit = async () => {
    const result = await addNote(content);
    if (!result.error) setContent("");
  };

  const startEditing = (noteId: string, value: string) => {
    setEditingNoteId(noteId);
    setEditingContent(value);
  };

  const cancelEditing = () => {
    setEditingNoteId(null);
    setEditingContent("");
  };

  const saveEditing = async (noteId: string) => {
    const nextContent = editingContent.trim();
    if (!nextContent) return;
    setSavingEditId(noteId);
    try {
      const result = await updateNote(noteId, nextContent);
      if (!result.error) cancelEditing();
    } finally {
      setSavingEditId(null);
    }
  };

  const confirmAndDelete = async (noteId: string) => {
    const accepted = window.confirm("Etes-vous sur de vouloir supprimer cette note ?");
    if (!accepted) return;
    setDeletingNoteId(noteId);
    try {
      await deleteNote(noteId);
      if (editingNoteId === noteId) cancelEditing();
    } finally {
      setDeletingNoteId(null);
    }
  };

  return (
    <section
      className={`rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 shadow-sm overflow-hidden ${className}`}
    >
      <div className="px-5 md:px-6 py-4 md:py-5 border-b border-slate-100 dark:border-white/10">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-200">
              <MessageSquareText size={18} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Journal de bord</h3>
              <p className="text-sm text-gray-500 dark:text-slate-400">{noteCountLabel}</p>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                Notes visibles par l&apos;admin, le partenaire et le commercial sur ce dossier.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-5 md:px-6 py-5 space-y-5">
        <div className="space-y-3">
          <textarea
            value={content}
            onChange={(event) => setContent(event.target.value)}
            placeholder="Ajouter une note rapide: NRP, rappel demain, client absent..."
            className="w-full min-h-[100px] resize-y rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-800/70 px-4 py-3 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500/20"
          />
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500 dark:text-slate-400">
              La note sera signee avec votre nom et votre role.
            </p>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
              Ajouter une note
            </button>
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 rounded-2xl bg-slate-50 dark:bg-white/[0.03] px-4 py-4 text-sm text-slate-500 dark:text-slate-300">
            <Loader2 size={16} className="animate-spin" />
            Chargement des notes...
          </div>
        ) : notes.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 bg-slate-50/70 dark:bg-white/[0.03] px-4 py-5 text-sm text-slate-500 dark:text-slate-400">
            Aucune note pour ce dossier pour le moment.
          </div>
        ) : (
          <div className="space-y-4">
            {notes.map((note, index) => {
              const formatted = formatNoteDate(note.createdAt);
              const canEdit = note.userId === currentUser.id;
              const canDelete = note.userId === currentUser.id || currentUser.role === "admin";
              const isEditing = editingNoteId === note.id;
              const isSaving = savingEditId === note.id;
              const isDeleting = deletingNoteId === note.id;
              const canSaveEdit = editingContent.trim().length > 0 && !isSaving;
              return (
                <article key={note.id} className="relative pl-7">
                  {index !== notes.length - 1 ? (
                    <span className="absolute left-[11px] top-8 h-[calc(100%+0.5rem)] w-px bg-slate-200 dark:bg-white/10" />
                  ) : null}
                  <span className="absolute left-0 top-2.5 h-[22px] w-[22px] rounded-full border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm" />
                  <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-slate-50/80 dark:bg-white/[0.03] px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                        [{roleLabelMap[note.userRole] ?? note.userRole}] : {note.userName} - le {formatted.date}
                        {formatted.time ? ` a ${formatted.time}` : ""}
                      </p>
                      {canEdit || canDelete ? (
                        <div className="flex items-center gap-1">
                          {canEdit ? (
                            <button
                              type="button"
                              onClick={() => startEditing(note.id, note.content)}
                              className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-slate-200"
                              title="Modifier la note"
                              disabled={isSaving || isDeleting}
                            >
                              <Edit2 size={14} />
                            </button>
                          ) : null}
                          {canDelete ? (
                            <button
                              type="button"
                              onClick={() => confirmAndDelete(note.id)}
                              className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-700 dark:text-slate-400 dark:hover:bg-red-500/15 dark:hover:text-red-300"
                              title="Supprimer la note"
                              disabled={isDeleting || isSaving}
                            >
                              {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    {isEditing ? (
                      <div className="mt-2 space-y-2">
                        <textarea
                          value={editingContent}
                          onChange={(event) => setEditingContent(event.target.value)}
                          className="w-full min-h-[88px] resize-y rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-indigo-500/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
                        />
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={cancelEditing}
                            className="inline-flex items-center gap-1 rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
                            disabled={isSaving}
                          >
                            <X size={14} />
                            Annuler
                          </button>
                          <button
                            type="button"
                            onClick={() => saveEditing(note.id)}
                            className="inline-flex items-center gap-1 rounded-xl bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={!canSaveEdit}
                          >
                            {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                            Enregistrer
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-700 dark:text-slate-300">
                        {note.content}
                      </p>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
